import { CrossBorderPaymentAction, CrossBorderPaymentActionType } from './actions';
import { crossBorderPaymentInitialState, CrossBorderPaymentState } from './state';

const reducer = (state: CrossBorderPaymentState, action: CrossBorderPaymentAction): CrossBorderPaymentState => {
    switch (action.type) {
        case CrossBorderPaymentActionType.FROM_ACCOUNT_SELECTED:
            return {
                ...state,
                fromAccount: action.payload,
            };
        case CrossBorderPaymentActionType.TO_ACCOUNT_SELECTED:
            return {
                ...state,
                receiver: action.payload,
                currency: undefined,
                newReceiver: false,
            };
        case CrossBorderPaymentActionType.AMOUNT:
            return {
                ...state,
                amount: action.payload,
            };
        case CrossBorderPaymentActionType.CURRENCY:
            return {
                ...state,
                currency: action.payload,
            };
        case CrossBorderPaymentActionType.TRANSFER_CURRENCY:
            return {
                ...state,
                transferCurrency: action.payload,
            };
        case CrossBorderPaymentActionType.PAYMENT_DATE:
            return {
                ...state,
                paymentDate: action.payload,
            };
        case CrossBorderPaymentActionType.MESSAGE_RECEIVER:
            return {
                ...state,
                messageReceiver: action.payload,
            };
        case CrossBorderPaymentActionType.FIRST_BANKING_DATE_FROM_TODAY:
            return {
                ...state,
                firstBankingDateFromToday: action.payload,
            };
        case CrossBorderPaymentActionType.SET_PAYMENT_DESCRIPTION:
            return {
                ...state,
                paymentDescription: action.payload,
            };
        case CrossBorderPaymentActionType.SET_REGULATORY_CODE:
            return {
                ...state,
                regulatoryCode: action.payload,
            };
        case CrossBorderPaymentActionType.SET_COUNTRY_INFO:
            return {
                ...state,
                chargeBearer: 'SHARED',
                country: action.payload,
                bankValidation: undefined,
                receiver: {
                    bankCountryCode: state.receiver?.bankCountryCode,
                    name: undefined,
                    accountNumber: undefined,
                    accountType: undefined,
                    address: undefined,
                    postcode: undefined,
                    town: undefined,
                    receiverCountryCode: undefined,
                    nationalId: undefined,
                    bic: undefined,
                },
                amount: undefined,
                amountInNok: undefined,
                currency: undefined,
            };
        case CrossBorderPaymentActionType.SET_CHOSEN_RECEIVER:
            return {
                ...state,
                chosenReceiver: action.payload,
            };
        case CrossBorderPaymentActionType.SET_RECEIVER_COUNTRY_INFO:
            return {
                ...state,
                country: action.payload,
            };
        case CrossBorderPaymentActionType.SET_RECEIVER_NAME:
            return {
                ...state,
                receiverName: action.payload,
            };
        case CrossBorderPaymentActionType.UPDATE_RECEIVER_NAME:
            return {
                ...state,
                receiver: {
                    ...state.receiver,
                    name: action.payload,
                },
            };
        case CrossBorderPaymentActionType.SET_RECEIVER_ACCOUNT_NUMBER:
            return {
                ...state,
                receiver: {
                    ...state.receiver,
                    accountNumber: action.payload,
                },
            };
        case CrossBorderPaymentActionType.SET_RECEIVER_ADDRESS:
            return {
                ...state,
                receiver: {
                    ...state.receiver,
                    address: action.payload,
                },
            };
        case CrossBorderPaymentActionType.SET_RECEIVER_ADDRESS_2:
            return {
                ...state,
                receiver: {
                    ...state.receiver,
                    address2: action.payload,
                },
            };
        case CrossBorderPaymentActionType.SET_RECEIVER_POSTCODE:
            return {
                ...state,
                receiver: {
                    ...state.receiver,
                    postcode: action.payload,
                },
            };
        case CrossBorderPaymentActionType.SET_RECEIVER_TOWN:
            return {
                ...state,
                receiver: {
                    ...state.receiver,
                    town: action.payload,
                },
            };
        case CrossBorderPaymentActionType.SET_RECEIVER_COUNTRY:
            return {
                ...state,
                receiver: {
                    ...state.receiver,
                    receiverCountryCode: action.payload,
                },
            };
        case CrossBorderPaymentActionType.SET_RECEIVER_ACCOUNT_TYPE:
            return {
                ...state,
                receiver: {
                    ...state.receiver,
                    accountType: action.payload,
                },
            };
        case CrossBorderPaymentActionType.SET_BANK_VALIDATION:
            return {
                ...state,
                bankValidation: action.payload,
            };
        case CrossBorderPaymentActionType.SET_BANK_COUNTRY:
            return {
                ...state,
                receiver: {
                    ...state.receiver,
                    bankCountryCode: action.payload,
                },
            };
        case CrossBorderPaymentActionType.SET_RECEIVER_NATIONAL_ID:
            return {
                ...state,
                receiver: {
                    ...state.receiver,
                    nationalId: action.payload,
                },
            };
        case CrossBorderPaymentActionType.SET_RECEIVER_BIC:
            return {
                ...state,
                receiver: {
                    ...state.receiver,
                    bic: action.payload,
                },
            };
        case CrossBorderPaymentActionType.RESET_RECEIVER:
            return {
                ...state,
                receiver: undefined,
                bankValidation: undefined,
                country: undefined,
                newReceiver: true,
            };
        case CrossBorderPaymentActionType.RESET_BANK_VALIDATION:
            return {
                ...state,
                bankValidation: undefined,
                receiver: {
                    ...state.receiver,
                    accountType: undefined,
                    nationalId: undefined,
                    bic: undefined,
                    needBic: false,
                },
            };
        case CrossBorderPaymentActionType.SET_AMOUNT_IN_NOK:
            return {
                ...state,
                amountInNok: action.payload,
            };
        case CrossBorderPaymentActionType.SET_NEED_BIC:
            return {
                ...state,
                receiver: {
                    ...state.receiver,
                    needBic: true,
                },
            };
        case CrossBorderPaymentActionType.SET_BANK_VALIDATION_OPTION:
            return {
                ...state,
                receiver: {
                    ...state.receiver,
                    bankValidationOption: action.payload,
                },
            };
        case CrossBorderPaymentActionType.SET_CHARGE_BEARER:
            return {
                ...state,
                chargeBearer: action.payload,
            };
        case CrossBorderPaymentActionType.SET_PRIORITY:
            return {
                ...state,
                priority: action.payload,
            };
        case CrossBorderPaymentActionType.RESET:
            return {
                ...crossBorderPaymentInitialState,
            };
        default:
            return state;
    }
};

const crossBorderPaymentReducer = (
    state: CrossBorderPaymentState,
    action: CrossBorderPaymentAction,
): CrossBorderPaymentState => {
    const newState = reducer(state, action);
    if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('%cPayment form', 'color: green; font-weight: bold', action, 'new state', newState);
    }
    return newState;
};

export default crossBorderPaymentReducer;
